// src/containers/pages/caseManagement/components/CommentTable.tsx

import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Tooltip,
} from 'components/mui';
import { Select, MenuItem } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getComparator, stableSort } from 'libs/sortList';
import type { CANtrackComment } from '../types';
import type { DestActivity } from '../types';

interface CommentTableProps {
  comments: CANtrackComment[];
  selectedIds: Set<number>;
  onToggleSelect: (commentId: number) => void;
  onToggleSelectAll: () => void;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  destinationActivities: DestActivity[];
  commentActivityMap: Record<number, number | null>;
  onSetCommentActivity: (commentId: number, activityId: number) => void;
  hasDestinationCase: boolean;
  applying: boolean;
  employeeFilterName: string; // for empty state message
}

type Order = 'asc' | 'desc';

const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

function CommentTable({
  comments,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allVisibleSelected,
  someVisibleSelected,
  destinationActivities,
  commentActivityMap,
  onSetCommentActivity,
  hasDestinationCase,
  applying,
  employeeFilterName,
}: CommentTableProps) {
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('dateValue');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset visible count when comments change
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [comments]);

  // Infinite scroll -- attach to .outlet ancestor (not window)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let scrollContainer: HTMLElement | null = el.parentElement;
    while (scrollContainer) {
      const { overflow, overflowY } = getComputedStyle(scrollContainer);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      )
        break;
      scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer!;
      const nearBottom =
        scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
      if (nearBottom && !isLoadingMore && visibleCount < comments.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) =>
            Math.min(prev + LOAD_MORE_COUNT, comments.length),
          );
          setIsLoadingMore(false);
        }, 100);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    return () => scrollContainer!.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visibleCount, comments.length]);

  // Sort handler
  const handleSort = (property: string) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Prepare sortable data with flattened properties for getComparator
  const sortableComments = useMemo(
    () =>
      comments.map((c) => ({
        ...c,
        activityTitle: c.gpda.activity.title ?? '',
        dateValue: c.date instanceof Date ? c.date.getTime().toString() : '0',
      })),
    [comments],
  );

  const sortedComments = useMemo(
    () => stableSort(sortableComments, getComparator(order, orderBy)),
    [sortableComments, order, orderBy],
  );

  const visibleComments = sortedComments.slice(0, visibleCount);

  const formatDate = (date: Date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD
  };

  const totalCols = 6; // checkbox + date + task desc + activity + dest activity + author

  // Empty state message
  const emptyMessage =
    employeeFilterName
      ? `No records found for ${employeeFilterName}`
      : 'No activities found for this case';

  return (
    <Paper ref={listRef} variant="outlined">
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/* Checkbox column */}
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  checked={allVisibleSelected}
                  onChange={onToggleSelectAll}
                  disabled={applying || comments.length === 0}
                  inputProps={{ 'aria-label': 'select all visible activities' }}
                />
              </TableCell>

              {/* Date */}
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'dateValue'}
                  direction={orderBy === 'dateValue' ? order : 'asc'}
                  onClick={handleSort('dateValue')}
                >
                  Date
                </TableSortLabel>
              </TableCell>

              {/* Task Description */}
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={handleSort('title')}
                >
                  Task Description
                </TableSortLabel>
              </TableCell>

              {/* Source Activity -- hidden on xs/sm */}
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <TableSortLabel
                  active={orderBy === 'activityTitle'}
                  direction={orderBy === 'activityTitle' ? order : 'asc'}
                  onClick={handleSort('activityTitle')}
                >
                  Activity
                </TableSortLabel>
              </TableCell>

              {/* Dest Activity -- always visible (primary purpose) */}
              <TableCell sx={{ minWidth: 200 }}>
                Dest Activity
              </TableCell>

              {/* Author -- hidden on xs */}
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <TableSortLabel
                  active={orderBy === 'consultantName'}
                  direction={orderBy === 'consultantName' ? order : 'asc'}
                  onClick={handleSort('consultantName')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleComments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleComments.map((comment) => {
                const isSelected = selectedIds.has(comment.id);
                const mappedActivityId =
                  commentActivityMap[comment.id] ?? null;
                const isMapped = mappedActivityId !== null;

                return (
                  <TableRow
                    key={comment.id}
                    hover
                    selected={isSelected}
                    sx={{
                      cursor: applying ? 'default' : 'pointer',
                      opacity: applying ? 0.6 : 1,
                    }}
                  >
                    {/* Checkbox */}
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleSelect(comment.id)}
                        disabled={applying}
                        inputProps={{
                          'aria-label': `select ${comment.title}`,
                        }}
                      />
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {formatDate(comment.date)}
                      </Typography>
                    </TableCell>

                    {/* Task Description */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {comment.title || '(no title)'}
                      </Typography>
                    </TableCell>

                    {/* Source Activity */}
                    <TableCell
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {comment.gpda.activity.title || 'Unmapped'}
                      </Typography>
                    </TableCell>

                    {/* Dest Activity Dropdown */}
                    <TableCell>
                      {hasDestinationCase ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Select
                            size="small"
                            value={mappedActivityId ?? ''}
                            onChange={(e) =>
                              onSetCommentActivity(
                                comment.id,
                                Number(e.target.value),
                              )
                            }
                            displayEmpty
                            fullWidth
                            disabled={applying}
                            sx={{ minWidth: 160 }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select activity...</em>
                            </MenuItem>
                            {destinationActivities.map((a) => (
                              <MenuItem key={a.id} value={a.id}>
                                {a.title}
                              </MenuItem>
                            ))}
                          </Select>
                          {isMapped ? (
                            <Tooltip title="Activity mapped">
                              <CheckCircleOutlineIcon
                                sx={{ fontSize: 18, color: 'success.main' }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="No destination activity selected">
                              <WarningAmberIcon
                                sx={{ fontSize: 18, color: 'warning.main' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Tooltip title="Select a destination case first">
                          <span>
                            <Select
                              size="small"
                              value=""
                              displayEmpty
                              fullWidth
                              disabled
                              sx={{ minWidth: 160, opacity: 0.5 }}
                            >
                              <MenuItem value="" disabled>
                                <em>Select dest case first</em>
                              </MenuItem>
                            </Select>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Author */}
                    <TableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {comment.consultantName || '--'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* "Showing X of Y" at bottom of table */}
      {comments.length > 0 && (
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {Math.min(visibleCount, comments.length)} of {comments.length} activities
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default CommentTable;
